// src/pages/SalesPage.tsx
// Офлайн-продажа на точке: сканер штрихкода или номер / модель с коробки.
// Товар в чек → "Оформить продажу" → запись в общий журнал ordered_products
// (status='sold', source='offline'), склад списывается.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Input, Button, Table, Tag, Select, Radio, message, Space, Popconfirm, InputNumber, Modal,
} from 'antd';
import { DeleteOutlined, ShoppingCartOutlined, EditOutlined } from '@ant-design/icons';
import ProductHeader from '../components/ProductHeader';
import { ProductPickerModal, BarcodeLinkPrompt, looksLikeBarcode } from '../components/ProductLookup';
import { useIsMobile } from '../hooks/useIsMobile';
import { apiSearchBySku } from '../services/productAPI';
import {
  createOfflineSale, fetchOfflineSales, deleteOfflineSaleLine, updateOfflineSaleLine,
} from '../services/orderSync';
import type { OfflineSaleLine, OfflineSaleRow } from '../services/orderSync';

const CATEGORY_LABELS: Record<string, string> = {
  spark_plugs:               'Свечи зажигания',
  ignition_coils:            'Катушки зажигания',
  crankshaft_sensors:        'Датчики',
  camshaft_sensors:          'Датчики',
  airbag_cables:             'Рулевые шлейфы',
  ignition_coil_mouthpieces: 'Наконечники и провода',
};

const fmt = (v: number) => `${Math.round(v).toLocaleString('ru-RU')} ֏`;

// Маска телефона: (099) 00 - 00 - 00. Не больше 9 цифр.
const formatPhoneMask = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  const len = d.length;
  if (len === 0) return '';
  let out = '(' + d.slice(0, Math.min(3, len));
  if (len >= 3) out += ')';
  if (len > 3) out += ' ' + d.slice(3, Math.min(5, len));
  if (len > 5) out += ' - ' + d.slice(5, Math.min(7, len));
  if (len > 7) out += ' - ' + d.slice(7, Math.min(9, len));
  return out;
};

type ProductType = 'original' | 'copy';

type CartLine = {
  key: string;
  product_id: number;
  brand: string;
  partNumber: string;
  model: string;
  sku: string;
  category_name: string;
  type: ProductType;
  priceOriginal: number;
  priceCopy: number;
  countOriginal: number;
  countCopy: number;
  discount: number;
  qty: number;
};

const unitPrice = (l: CartLine) =>
  l.type === 'original' ? l.priceOriginal : l.priceCopy;

const availableStock = (l: CartLine) =>
  l.type === 'original' ? l.countOriginal : l.countCopy;

const lineSum = (l: CartLine) => {
  const gross = unitPrice(l) * l.qty;
  return l.discount ? gross - Math.floor((l.discount / 100) * gross) : gross;
};

const pickDefaultType = (p: any): ProductType => {
  const hasOrig = Number(p.price_original) > 0 || Number(p.count_original) > 0;
  const hasCopy = Number(p.price_copy) > 0 || Number(p.count_copy) > 0;
  return !hasOrig && hasCopy ? 'copy' : 'original';
};

const SalesPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [skuInput, setSkuInput]   = useState('');
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound]   = useState(false);
  const [notFoundQuery, setNotFoundQuery] = useState('');
  const [candidates, setCandidates]       = useState<any[]>([]);   // нашлось несколько — выбрать
  const [pickerQuery, setPickerQuery]     = useState('');
  const [pendingBarcode, setPendingBarcode] = useState<string | null>(null); // скан, который не нашёлся
  const [linkProduct, setLinkProduct]     = useState<any | null>(null);    // кому предложить его привязать
  const [cart, setCart]           = useState<CartLine[]>([]);
  const [phone, setPhone]         = useState('');
  const [payment, setPayment]     = useState<'cash' | 'card'>('cash');
  const [saving, setSaving]       = useState(false);
  const [history, setHistory]     = useState<OfflineSaleRow[]>([]);
  const [editRow, setEditRow]     = useState<OfflineSaleRow | null>(null);
  const [editQty, setEditQty]     = useState(1);
  const [editPayment, setEditPayment] = useState<'cash' | 'card'>('cash');
  const [editSaving, setEditSaving]   = useState(false);
  const inputRef = useRef<any>(null);

  const refocus = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await fetchOfflineSales());
    } catch { /* история необязательна для работы продажи */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const addToCart = useCallback((p: any) => {
    const productId = Number(p.id ?? p.product_id);
    const type = pickDefaultType(p);
    setCart((prev) => {
      const key = `${productId}_${type}`;
      const existing = prev.find((l) => l.key === key);
      if (existing) {
        // тот же товар того же типа — увеличиваем количество
        return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + 1 } : l));
      }
      const line: CartLine = {
        key,
        product_id:    productId,
        brand:         p.brand        ?? '',
        partNumber:    p.partNumber   ?? p.detail_number ?? '',
        model:         p.model        ?? '',
        sku:           p.sku          ?? '',
        category_name: p.category_name ?? '',
        type,
        priceOriginal: Number(p.price_original) || 0,
        priceCopy:     Number(p.price_copy) || 0,
        countOriginal: Number(p.count_original) || 0,
        countCopy:     Number(p.count_copy) || 0,
        discount:      Number(p.discount) || 0,
        qty:           1,
      };
      return [...prev, line];
    });
  }, []);

  // Товар выбран (единственный найденный или из списка). Если до этого скан не нашёлся,
  // а товар без штрихкода — предложить привязать тот скан к нему.
  const choose = useCallback((p: any) => {
    addToCart(p);
    setCandidates([]);
    setSkuInput('');
    // Непривязанный скан относится только к следующему выбранному товару;
    // новый выбор, пока вопрос висит без ответа, вопрос снимает.
    if (!linkProduct && pendingBarcode && p.matched_by !== 'sku' && !p.sku) {
      setLinkProduct(p);
    } else {
      setPendingBarcode(null);
      setLinkProduct(null);
    }
    refocus();
  }, [addToCart, linkProduct, pendingBarcode, refocus]);

  const handleScan = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    setSearching(true);
    setNotFound(false);
    try {
      const results = await apiSearchBySku(q);
      if (results.length === 1) {
        choose(results[0]);
      } else if (results.length > 1) {
        setPickerQuery(q);
        setCandidates(results);
      } else {
        setNotFound(true);
        setNotFoundQuery(q);
        if (looksLikeBarcode(q)) {
          setPendingBarcode(q);
          setLinkProduct(null);
          setSkuInput('');   // скан — поле под следующий ввод; набранный номер остаётся для правки
        }
      }
    } catch {
      setNotFound(true);
      setNotFoundQuery(q);
    } finally {
      setSearching(false);
      refocus();
    }
  }, [choose, refocus]);

  const setLineType = useCallback((key: string, type: ProductType) => {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, key: `${l.product_id}_${type}`, type } : l)));
  }, []);

  const setLineQty = useCallback((key: string, qty: number) => {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, qty: Math.max(1, qty || 1) } : l)));
  }, []);

  const removeLine = useCallback((key: string) => {
    setCart((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const total = cart.reduce((acc, l) => acc + lineSum(l), 0);
  const positions = cart.reduce((acc, l) => acc + l.qty, 0);

  const handleCheckout = useCallback(async () => {
    if (cart.length === 0) return;
    const lines: OfflineSaleLine[] = cart.map((l) => ({
      product_id:   l.product_id,
      product_type: l.type,
      count:        l.qty,
    }));
    setSaving(true);
    try {
      const result = await createOfflineSale({
        lines,
        phone: phone.trim() || undefined,
        payment_method: payment,
      });
      message.success(`Продажа оформлена: ${positions} шт. на ${fmt(result.total)}`);
      setCart([]);
      setPhone('');
      setPayment('cash');
      loadHistory();
      refocus();
    } catch (e: any) {
      message.error(e?.message || 'Ошибка оформления продажи');
    } finally {
      setSaving(false);
    }
  }, [cart, phone, payment, positions, loadHistory, refocus]);

  const handleDeleteSale = useCallback(async (id: number) => {
    try {
      await deleteOfflineSaleLine(id);
      message.success('Продажа удалена, товар возвращён на склад');
      loadHistory();
    } catch (e: any) {
      message.error(e?.message || 'Ошибка удаления продажи');
    }
  }, [loadHistory]);

  const openEdit = useCallback((row: OfflineSaleRow) => {
    setEditRow(row);
    setEditQty(row.count);
    setEditPayment(row.payment_method);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editRow) return;
    setEditSaving(true);
    try {
      await updateOfflineSaleLine(editRow.id, { count: editQty, payment_method: editPayment });
      message.success('Продажа изменена');
      setEditRow(null);
      loadHistory();
    } catch (e: any) {
      message.error(e?.message || 'Ошибка изменения продажи');
    } finally {
      setEditSaving(false);
    }
  }, [editRow, editQty, editPayment, loadHistory]);

  const cartColumns = [
    {
      title: 'Товар', key: 'product',
      render: (_: any, l: CartLine) => (
        <span>
          <strong>{l.brand} {l.partNumber}</strong>
          {l.model && <span style={{ color: '#8c8c8c', marginLeft: 6 }}>/ {l.model}</span>}
          {l.sku && <Tag style={{ marginLeft: 8 }}>SKU: {l.sku}</Tag>}
          {l.category_name && (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {CATEGORY_LABELS[l.category_name] ?? l.category_name}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Тип', key: 'type', width: 130,
      render: (_: any, l: CartLine) => (
        <Select
          value={l.type}
          style={{ width: 110 }}
          onChange={(v: ProductType) => setLineType(l.key, v)}
          options={[
            { value: 'original', label: 'Оригинал' },
            { value: 'copy',     label: 'Копия' },
          ]}
        />
      ),
    },
    {
      title: 'Цена', key: 'price', width: 110, align: 'right' as const,
      render: (_: any, l: CartLine) => fmt(unitPrice(l)),
    },
    {
      title: 'Кол-во', key: 'qty', width: 130,
      render: (_: any, l: CartLine) => (
        <Space size={4}>
          <InputNumber
            min={1}
            value={l.qty}
            onChange={(v) => setLineQty(l.key, Number(v))}
            style={{ width: 70 }}
          />
          {l.qty > availableStock(l) && (
            <Tag color="warning" style={{ margin: 0 }}>склад: {availableStock(l)}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Сумма', key: 'sum', width: 120, align: 'right' as const,
      render: (_: any, l: CartLine) => <b>{fmt(lineSum(l))}</b>,
    },
    {
      title: '', key: 'actions', width: 50, align: 'center' as const,
      render: (_: any, l: CartLine) => (
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeLine(l.key)} />
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Дата', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: (v: string) => new Date(v).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      }),
    },
    {
      title: 'Товар', key: 'product',
      render: (_: any, r: OfflineSaleRow) => (
        <span>
          <strong>{r.brand} {r.detail_number}</strong>
          {r.model && <span style={{ color: '#8c8c8c', marginLeft: 6 }}>/ {r.model}</span>}
        </span>
      ),
    },
    {
      title: 'Тип', dataIndex: 'product_type', key: 'product_type', width: 90,
      render: (v: 'original' | 'copy') => (v === 'copy' ? 'Копия' : 'Оригинал'),
    },
    { title: 'Кол-во', dataIndex: 'count', key: 'count', width: 80 },
    {
      title: 'Сумма', dataIndex: 'sum', key: 'sum', width: 110, align: 'right' as const,
      render: (v: number) => <b>{fmt(v)}</b>,
    },
    {
      title: 'Оплата', dataIndex: 'payment_method', key: 'payment_method', width: 100,
      render: (v: 'cash' | 'card') => (
        <Tag color={v === 'card' ? 'geekblue' : 'green'}>{v === 'card' ? 'Карта' : 'Наличные'}</Tag>
      ),
    },
    { title: 'Чек', dataIndex: 'ref', key: 'ref', width: 160 },
    {
      title: '', key: 'actions', width: 90, align: 'center' as const,
      render: (_: any, r: OfflineSaleRow) => (
        <Space size={8}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm
            title="Удалить продажу? Товар вернётся на склад."
            okText="Да" cancelText="Нет"
            onConfirm={() => handleDeleteSale(r.id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <ProductHeader title="Продажа" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input
          ref={inputRef}
          autoFocus
          placeholder="Штрихкод, модель или номер детали"
          value={skuInput}
          onChange={(e) => setSkuInput(e.target.value)}
          onPressEnter={() => handleScan(skuInput)}
          style={isMobile ? { flex: 1, minWidth: 0 } : { width: 340 }}
          allowClear
        />
        <Button type="primary" loading={searching} onClick={() => handleScan(skuInput)}>
          Добавить
        </Button>
      </div>

      {notFound && (
        <div style={{ marginBottom: 16 }}>
          <Tag color="error" style={{ whiteSpace: 'normal' }}>
            {looksLikeBarcode(notFoundQuery)
              ? `Штрихкод ${notFoundQuery} ещё не привязан — введите модель или номер детали с коробки`
              : `«${notFoundQuery}» не найден — проверьте модель или номер детали`}
          </Tag>
        </div>
      )}

      <BarcodeLinkPrompt
        barcode={pendingBarcode}
        product={linkProduct}
        onLinked={(code, p) => {
          const id = Number(p.id ?? p.product_id);
          setCart((prev) => prev.map((l) => (l.product_id === id ? { ...l, sku: code } : l)));
        }}
        onDone={() => { setPendingBarcode(null); setLinkProduct(null); refocus(); }}
      />

      <ProductPickerModal
        query={pickerQuery}
        items={candidates}
        onPick={choose}
        onCancel={() => { setCandidates([]); refocus(); }}
      />

      <Table
        dataSource={cart}
        columns={cartColumns}
        rowKey="key"
        size="small"
        pagination={false}
        scroll={isMobile ? { x: 'max-content' } : undefined}
        locale={{ emptyText: 'Чек пуст — отсканируйте товар или введите его номер' }}
      />

      {cart.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: '16px',
            background: '#141414',
            color: '#fff',   // «Итого» было тёмным по тёмному — не читалось (17.10.2026)
            borderRadius: 8,
            border: '1px solid #303030',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div style={{ fontSize: 18 }}>
            Итого: <strong>{fmt(total)}</strong>
            <span style={{ color: '#8c8c8c', marginLeft: 8 }}>({positions} шт.)</span>
          </div>

          <Input
            placeholder="(099) 00 - 00 - 00"
            value={phone}
            onChange={(e) => setPhone(formatPhoneMask(e.target.value))}
            style={{ width: 240 }}
            inputMode="numeric"
            allowClear
          />

          <Radio.Group
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: 'cash', label: 'Наличные' },
              { value: 'card', label: 'Карта' },
            ]}
          />

          <Space style={{ marginLeft: 'auto' }}>
            <Popconfirm title="Очистить чек?" okText="Да" cancelText="Нет" onConfirm={() => setCart([])}>
              <Button>Очистить</Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              loading={saving}
              onClick={handleCheckout}
            >
              Оформить продажу
            </Button>
          </Space>
        </div>
      )}

      {history.length > 0 && (
        <>
          <h3 style={{ marginTop: 32, marginBottom: 12 }}>История продаж</h3>
          <Table
            dataSource={history}
            columns={historyColumns}
            rowKey={(r) => String(r.id)}
            size="small"
            scroll={isMobile ? { x: 'max-content' } : undefined}
            pagination={{ pageSize: 20, position: ['bottomCenter'], showSizeChanger: false }}
          />
        </>
      )}

      <Modal
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={handleEditSave}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={editSaving}
        title="Изменить продажу"
        destroyOnHidden
      >
        {editRow && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 16 }}>
              <strong>{editRow.brand} {editRow.detail_number}</strong>
              {editRow.model && <span style={{ color: '#8c8c8c', marginLeft: 6 }}>/ {editRow.model}</span>}
              <Tag style={{ marginLeft: 8 }}>{editRow.product_type === 'copy' ? 'Копия' : 'Оригинал'}</Tag>
            </div>
            <Space size={24} wrap>
              <span>
                Количество:{' '}
                <InputNumber min={1} value={editQty} onChange={(v) => setEditQty(Number(v) || 1)} style={{ width: 90 }} />
              </span>
              <span>
                Оплата:{' '}
                <Select
                  value={editPayment}
                  style={{ width: 130 }}
                  onChange={(v) => setEditPayment(v)}
                  options={[
                    { value: 'cash', label: 'Наличные' },
                    { value: 'card', label: 'Карта' },
                  ]}
                />
              </span>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalesPage;
